import { createReducer } from '@reduxjs/toolkit'
import {
  LoadingStatus,
  Country,
  ValidationType,
  Language,
  Resource,
} from 'shared/types'
import { initResource, setPending, setSucceeded, setFailed } from 'shared/utils'
import * as actions from './actions'

interface State {
  loadingState: Record<'settings', LoadingStatus>
  countries: Country[] | null
  languages: Resource<Language[]>
  selectedLanguage: Resource<Language | null>
  updateLanguage: Resource
  error?: string | null
}

const initialState: State = {
  loadingState: {
    settings: LoadingStatus.Idle,
  },
  countries: null,
  error: null,
  languages: initResource([]),
  selectedLanguage: initResource(null),
  updateLanguage: initResource(),
}

export default createReducer(initialState, builder =>
  builder
    .addCase(actions.getCountries.pending, state => {
      state.loadingState.settings = LoadingStatus.Pending
      state.error = initialState.error
      state.countries = null
    })
    .addCase(
      actions.getCountries.fulfilled,
      (state, { payload: { response } }) => {
        state.loadingState.settings = LoadingStatus.Succeeded
        state.error = null
        state.countries = response
      }
    )
    .addCase(actions.getCountries.rejected, (state, action) => {
      state.loadingState.settings = LoadingStatus.Failed
      state.error = action.error.message
    })
    .addCase(actions.getValidation.pending, state => {
      state.loadingState.settings = LoadingStatus.Pending
      state.error = initialState.error
      state.countries = null
    })
    .addCase(
      actions.getValidation.fulfilled,
      (state, { payload: { response } }) => {
        Object.keys(response).forEach(key =>
          sessionStorage.setItem(key, response[key as ValidationType] as string)
        )
        state.loadingState.settings = LoadingStatus.Succeeded
        state.error = null
      }
    )
    .addCase(actions.getValidation.rejected, (state, action) => {
      state.loadingState.settings = LoadingStatus.Failed
      state.error = action.error.message
    })
    .addCase(actions.getLanguages.pending, state => {
      setPending(state.languages)
    })
    .addCase(actions.getLanguages.fulfilled, (state, { payload }) => {
      setSucceeded(state.languages, payload)
    })
    .addCase(actions.getLanguages.rejected, (state, action) => {
      setFailed(state.languages, action.error.message)
    })
    .addCase(actions.getSelectedLanguage.pending, state => {
      setPending(state.selectedLanguage)
    })
    .addCase(actions.getSelectedLanguage.fulfilled, (state, { payload }) => {
      setSucceeded(state.selectedLanguage, payload)
    })
    .addCase(actions.getSelectedLanguage.rejected, (state, action) => {
      setFailed(state.selectedLanguage, action.error.message)
    })
    .addCase(actions.setLanguage.pending, state => {
      setPending(state.updateLanguage)
    })
    .addCase(actions.setLanguage.fulfilled, (state, { meta: { arg } }) => {
      setSucceeded(state.updateLanguage)
      const selectedLanguage = state.languages.data.find(l => l.id === arg)
      if (selectedLanguage) {
        state.selectedLanguage.data = selectedLanguage
      }
    })
    .addCase(actions.setLanguage.rejected, (state, action) => {
      setFailed(state.updateLanguage, action.error.message)
    })
)
